import { NavMain } from "@shared/layouts/nav-main";
import { NavUser } from "@/features/users/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarGroup,
  SidebarGroupLabel,
} from "@shared/components/ui/sidebar";
import Logo from "@shared/components/Logo";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { navigationConfig } from "@/lib/constant";
import { useBadgeNumbers } from "@shared/hooks/use-badge-numbers";

export function AppSidebar({ ...props }) {
  const auth = useAuth().user?.role;
  const { t } = useTranslation();
  const { badgeNumbers, badgeVisibility, isLoading } = useBadgeNumbers();

  const currentNav = navigationConfig[auth] || navigationConfig.client;

  // Add badge numbers to navigation items
  const enhanceNavWithBadges = (navConfig) => {
    return Object.entries(navConfig).reduce((acc, [groupKey, items]) => {
      acc[groupKey] = items.map((item) => {
        const enhancedItem = { ...item };

        // Check for Livreur Management pages FIRST (more specific URLs)
        if (
          item.url === "/packages/livreur-management" &&
          badgeVisibility.unassignedPackages
        ) {
          enhancedItem.badge = badgeNumbers.unassignedPackages;
        } else if (
          item.url === "/pickups/livreur-management" &&
          badgeVisibility.unassignedPickups
        ) {
          enhancedItem.badge = badgeNumbers.unassignedPickups;
        }
        // Then check for general pages
        else if (item.url?.includes("/packages") && badgeVisibility.packages) {
          enhancedItem.badge = badgeNumbers.packages;
        } else if (item.url?.includes("/pickups") && badgeVisibility.pickups) {
          enhancedItem.badge = badgeNumbers.pickups;
        } else if (
          item.url?.includes("/invoices") &&
          badgeVisibility.invoices
        ) {
          enhancedItem.badge = badgeNumbers.invoices;
        } else if (item.url?.includes("/tickets") && badgeVisibility.tickets) {
          enhancedItem.badge = badgeNumbers.tickets;
        } else if (
          item.url?.includes("/return-voucher") &&
          badgeVisibility.returnVouchers
        ) {
          enhancedItem.badge = badgeNumbers.returnVouchers;
        } else if (item.url?.includes("/users") && badgeVisibility.users) {
          enhancedItem.badge = badgeNumbers.users;
        }

        return enhancedItem;
      });
      return acc;
    }, {});
  };

  const navWithBadges = enhanceNavWithBadges(currentNav);

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-sidebar-border/50"
      {...props}
    >
      <SidebarHeader className="border-b border-sidebar-border/50 p-4 sidebar-header">
        <Logo className={"h-12"} />
      </SidebarHeader>
      <SidebarContent className="scrollbar-hide px-2 py-4 sidebar-content">
        <div className="space-y-6 overflow-y-auto">
          {Object.entries(navWithBadges).map(([groupKey, items]) => (
            <SidebarGroup key={groupKey}>
              <SidebarGroupLabel className="text-xs font-semibold text-sidebar-foreground/60 uppercase tracking-wider mb-3 sidebar-group-label">
                {groupKey === "main"
                  ? t("sidebar.dashboard")
                  : groupKey === "operations"
                  ? t("sidebar.operations")
                  : groupKey === "inventory"
                  ? t("sidebar.inventory")
                  : groupKey === "financial"
                  ? t("sidebar.financial")
                  : groupKey === "delivery"
                  ? t("sidebar.delivery")
                  : groupKey === "management"
                  ? t("sidebar.management")
                  : groupKey === "support"
                  ? t("sidebar.support")
                  : groupKey}
              </SidebarGroupLabel>
              <NavMain items={items} />
            </SidebarGroup>
          ))}
        </div>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border/50 p-4 sidebar-footer relative">
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
